import { useState } from "react";
import { Search, Package, Landmark } from "lucide-react";
import { orderStatus, euro, ORDER_STATUS_META, ORDER_FLOW } from "@/lib/store";
import { Seo } from "@/components/Seo";

export default function OrderStatus() {
  const [num, setNum] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const lookup = async (e) => {
    e?.preventDefault();
    setErr(""); setOrder(null);
    try { setOrder(await orderStatus(num.trim(), email.trim())); }
    catch (e) { setErr(e.response?.data?.detail || "Ordine non trovato"); }
  };
  const stepIdx = order ? ORDER_FLOW.indexOf(order.status) : -1;
  return (
    <main className="mx-auto min-h-screen max-w-2xl px-4 py-12">
      <Seo title="Stato del tuo ordine · iPhix Scafati" noindex />
      <h1 className="text-3xl font-extrabold sm:text-4xl">Stato del tuo ordine</h1>
      <p className="mt-2 text-sm text-zinc-400">Inserisci il numero ordine (es. ORD5001) e l'email usata in fase di acquisto.</p>
      <form onSubmit={lookup} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <input data-testid="order-number-input" value={num} onChange={(e) => setNum(e.target.value)} placeholder="Numero ordine" className="flex-1 rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#2563EB] focus:outline-none" />
        <input data-testid="order-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email dell'ordine" className="flex-1 rounded-xl border border-white/10 bg-[#11141A] px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#2563EB] focus:outline-none" />
        <button data-testid="order-lookup-btn" className="flex items-center justify-center gap-2 rounded-xl bg-[#2563EB] px-5 py-3 font-semibold text-white"><Search className="h-4 w-4" /> Cerca</button>
      </form>
      {err && <p data-testid="order-error" className="mt-4 text-rose-400">{err}</p>}

      {order && (
        <div data-testid="order-detail" className="mt-8 space-y-6">
          <div className="rounded-2xl border border-white/10 bg-[#11141A] p-5">
            <div className="flex items-center gap-3"><Package className="h-5 w-5 text-[#2563EB]" />
              <span className="font-mono text-lg font-bold text-white">{order.order_number}</span>
              <span className="ml-auto rounded-full bg-[#2563EB]/20 px-3 py-1 text-sm text-[#60a5fa]">{ORDER_STATUS_META[order.status] || order.status}</span>
            </div>
            <div className="mt-4 space-y-1 text-sm">
              {order.items.map((i, j) => <div key={j} className="flex justify-between text-zinc-300"><span>{i.qty}× {i.name} {i.storage || ""} {i.color || ""}</span><span>{euro(i.unit_price_cents * i.qty)}</span></div>)}
              <div className="mt-2 flex justify-between border-t border-white/10 pt-2 font-bold text-white"><span>Totale</span><span>{euro(order.total_cents)}</span></div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#11141A] p-5">
            <h3 className="mb-4 text-sm font-semibold text-zinc-400">Avanzamento</h3>
            <ol className="space-y-2">
              {ORDER_FLOW.filter((s) => s !== "ANNULLATO").map((s, i) => (
                <li key={s} className="flex items-center gap-3 text-sm">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${i <= stepIdx ? "bg-emerald-500 text-white" : "bg-white/10 text-zinc-500"}`}>{i < stepIdx ? "✓" : i + 1}</span>
                  <span className={i <= stepIdx ? "text-white" : "text-zinc-500"}>{ORDER_STATUS_META[s]}</span>
                </li>
              ))}
            </ol>
          </div>

          {order.bank && order.bank.configurato && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
              <div className="flex items-center gap-2 text-amber-300"><Landmark className="h-5 w-5" /><h3 className="font-semibold">Istruzioni bonifico</h3></div>
              <div className="mt-3 grid grid-cols-1 gap-1 text-sm text-zinc-300">
                <div className="flex justify-between"><span className="text-zinc-500">Intestatario</span><span>{order.bank.intestatario}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">IBAN</span><span className="font-mono">{order.bank.iban}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Causale</span><span className="font-mono font-bold text-white">{order.bank.causale}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Importo</span><span className="font-bold text-white">{order.bank.importo}</span></div>
              </div>
              <p className="mt-3 text-xs text-zinc-500">{order.bank.istruzioni}</p>
            </div>
          )}
          {order.shipment && (
            <div className="rounded-2xl border border-white/10 bg-[#11141A] p-5" data-testid="order-shipment">
              <h3 className="mb-2 text-sm font-semibold text-zinc-400">Spedizione</h3>
              <div className="grid gap-1 text-sm text-zinc-300">
                <div className="flex justify-between"><span className="text-zinc-500">Stato</span><span>{order.shipment.status}</span></div>
                {order.shipment.carrier && <div className="flex justify-between"><span className="text-zinc-500">Corriere</span><span>{order.shipment.carrier}</span></div>}
                {order.shipment.tracking_number && <div className="flex justify-between"><span className="text-zinc-500">Tracking</span><span className="font-mono">{order.shipment.tracking_number}</span></div>}
                {order.shipment.insured && <div className="flex justify-between"><span className="text-zinc-500">Assicurata</span><span>Sì</span></div>}
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
